import { useEffect, useRef, useState } from 'react';

import axios from 'axios';
import styled from 'styled-components';

import { SERVER_ADDRESS } from './server';
import { GTM_EVENTS, pushGtmEvent } from './analytics/gtm';

export default function AwakeExchange({ provider = 'awake' }: { provider?: 'awake' | 'kakao' }) {

  const [tokens] = useState(() => {

    const query = new URLSearchParams(window.location.search);

    return { accountToken: query.get('accountToken'), ssoToken: query.get('ssoToken'), error: query.get('error') };

  });

  const pending = useRef<Promise<any> | null>(null);

  const [error, setError] = useState('');
  const [slow, setSlow] = useState(false);

  const retry = provider === 'kakao' ? '팬버드에서 카카오 로그인을 다시 진행해주세요.' : 'AWAKE에서 다시 열어주세요.';

  useEffect(() => {

    const clean = new URL(window.location.href);

    clean.searchParams.delete('accountToken'); clean.searchParams.delete('ssoToken'); clean.searchParams.delete('error');

    window.history.replaceState({}, '', clean.pathname + clean.search);

    if (tokens.error) {

      if (provider !== 'kakao') setError('로그인에 실패했습니다. AWAKE에서 다시 열어주세요.');

      else {

        const suffix = tokens.error !== 'LOGIN_FAILED' ? ` (${tokens.error})` : '';

        setError(`카카오 로그인에 실패했습니다${suffix}. 팬버드에서 카카오 로그인을 다시 진행해주세요.`);

      }

      return;

    }

    const restoreSession = provider === 'kakao' && !tokens.accountToken && !tokens.ssoToken;

    if (!restoreSession && (!tokens.accountToken || !tokens.ssoToken)) {

      setError(`로그인 정보가 없습니다. ${retry}`);

      return;

    }

    let active = true;
    const slowTimer = window.setTimeout(() => { if (active) setSlow(true); }, 6000);

    const payload = { accountToken: tokens.accountToken, ssoToken: tokens.ssoToken };

    let returnTo = '/';

    try { returnTo = sessionStorage.getItem('authReturnTo') || '/'; } catch { /* Cookies can restore the session without storage. */ }

    const safePath = (path: unknown) => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !/^\/auth(?:[/?#]|$)/.test(path) ? path : '/';

    if (!pending.current) pending.current = axios.post(

      `${SERVER_ADDRESS}user/${restoreSession ? 'refresh' : `${provider}/exchange`}`,

      restoreSession ? {} : payload, { withCredentials: true, timeout: 15000 });

    pending.current.then(({ data }) => {

      if (!active) return;
      window.clearTimeout(slowTimer);

      if (!data.newRefreshToken) throw new Error('로그인 결과가 없습니다.');

      localStorage.removeItem('key'); localStorage.removeItem('authority');

      sessionStorage.removeItem('authority');

      sessionStorage.setItem('key', data.newRefreshToken);
      if (typeof data.joinRoute === 'string') sessionStorage.setItem('join_route', data.joinRoute);
      if (provider === 'awake' && data.joinRoute === 'awake' && data.sellerId && data.userId) {
        pushGtmEvent(data.authEvent === 'join' ? GTM_EVENTS.awakeJoin : GTM_EVENTS.awakeLogin, data.sellerId, data.userId);
      }
      sessionStorage.removeItem('authReturnTo');

      const path = restoreSession ? returnTo : data.redirectPath;

      window.location.replace(safePath(path));

    }).catch(err => {

      if (!active) return;
      window.clearTimeout(slowTimer);

      if (restoreSession && err?.response?.status === 401) {

        const login = new URL(`${SERVER_ADDRESS}user/login/kakaoSync`);

        login.searchParams.set('nextPath', safePath(returnTo));

        window.location.replace(login.href);

        return;

      }

      setError(err?.code === 'ECONNABORTED'
        ? '로그인 응답이 늦어지고 있습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.'
        : err?.response?.data?.message || `로그인 링크를 사용할 수 없습니다. ${retry}`);

    });

    return () => { active = false; window.clearTimeout(slowTimer); };

  }, [tokens, provider, retry]);

  return <AuthPage>
    <Brand href="/" aria-label="팬버드 홈으로">
      <img src={require('../Image/Logo_Text.png')} alt="Fanbird" width="144" />
    </Brand>
    <Content aria-busy={!error}>
      <Indicator aria-hidden="true">
        {error ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 7v6m0 3v1" /><circle cx="12" cy="12" r="9" /></svg> : <span />}
      </Indicator>
      <h1>{error ? '로그인을 완료하지 못했어요' : '로그인 확인 중입니다.'}</h1>
      <p role={error ? 'alert' : 'status'}>{error || (slow
        ? '연결이 평소보다 조금 늦어지고 있어요. 잠시만 기다려주세요.'
        : '잠시만 기다려주세요. 확인이 끝나면 팬버드로 이동합니다.')}</p>
      {error && <Actions>
        {provider === 'kakao' && <a className="primary" href={`${SERVER_ADDRESS}user/login/kakaoSync?nextPath=%2F`}>카카오 로그인 다시 시도</a>}
        <a href="/">팬버드 홈으로</a>
      </Actions>}
    </Content>
    <Footer>좋아하는 스토어와 만나는 곳, 팬버드</Footer>
  </AuthPage>;
}

const AuthPage = styled.main`
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px 28px;
  background: var(--user-surface-muted);
  color: var(--user-text);
  font-family: var(--font-Pretendard-Regular), sans-serif;
  a:focus-visible { outline: 3px solid #7955f0; outline-offset: 5px; }
  ::selection { background: var(--user-accent-soft); color: var(--user-text); }
  @media (max-width: 480px) { padding-top: 36px; }
`;
const Brand = styled.a`
  display: block;
  padding: 8px;
  img { display: block; width: 144px; height: auto; }
`;
const Content = styled.section`
  width: 100%;
  max-width: 400px;
  margin: auto 0;
  padding: 64px 0;
  text-align: center;
  h1 { margin: 28px 0 12px; font-family: var(--font-Pretendard-SemiBold), sans-serif; font-size: clamp(22px, 5vw, 28px); line-height: 1.4; letter-spacing: -0.025em; word-break: keep-all; }
  p { margin: 0 auto; max-width: 330px; font-size: 15px; line-height: 1.75; color: var(--user-text-muted); word-break: keep-all; overflow-wrap: anywhere; }
`;
const Indicator = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--user-accent);
  background: var(--user-accent-soft);
  svg { width: 32px; height: 32px; }
  span { width: 30px; height: 30px; border: 3px solid var(--user-border); border-top-color: var(--user-accent); border-radius: 50%; animation: auth-spin 900ms linear infinite; }
  @keyframes auth-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { span { animation: none; } }
`;
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 32px auto 0;
  max-width: 300px;
  a { min-height: 48px; display: flex; align-items: center; justify-content: center; padding: 0 16px; border-radius: 12px; color: var(--user-text-muted); font-size: 15px; text-decoration: none; }
  a:hover { background: var(--user-surface-raised); }
  a:active { transform: translateY(1px); }
  a.primary { background: #7955f0; color: #fff; font-family: var(--font-Pretendard-SemiBold), sans-serif; }
  a.primary:hover { background: #6742d9; }
`;
const Footer = styled.footer`
  font-size: 12px;
  line-height: 1.6;
  color: var(--user-text-muted);
  text-align: center;
`;
